<template>
  <div class="custom-field" :class="{ required: isRequired }">
    <v-text-field
      v-if="textResponse"
      v-model="localValue"
      :title="field.attributes.name"
      v-bind="customFieldErrorAttrs"
      @keyup="change"
    >
      <template #message="{ message }">
        <span
          :id="errorId(customFieldId)"
          role="alert"
          :data-testid="errorTestid('custom')"
          >{{ message }}</span
        >
      </template>
      <template #label>
        <span v-if="isRequired" class="red--text">*</span>
        <span>{{ field.attributes.name }}</span>
      </template>
    </v-text-field>
    <v-textarea
      v-if="paragraphResponse"
      v-model="localValue"
      v-bind="customFieldErrorAttrs"
      @keyup="change"
    >
      <template #message="{ message }">
        <span
          :id="errorId(customFieldId)"
          role="alert"
          :data-testid="errorTestid('custom')"
          >{{ message }}</span
        >
      </template>
      <template #label>
        <span v-if="isRequired" class="red--text">*</span>
        <span>{{ field.attributes.name }}</span>
      </template>
    </v-textarea>
    <v-text-field
      v-else-if="numberResponse"
      type="number"
      v-model="localValue"
      v-bind="customFieldErrorAttrs"
      @input="change"
    >
      <template #message="{ message }">
        <span
          :id="errorId(customFieldId)"
          role="alert"
          :data-testid="errorTestid('custom')"
          >{{ message }}</span
        >
      </template>
      <template #label>
        <span v-if="isRequired" class="red--text">*</span>
        <span>{{ field.attributes.name }}</span>
      </template>
    </v-text-field>
    <v-checkbox
      v-else-if="checkboxResponse"
      v-model="localValue"
      @change="change"
    >
      <template #label>
        <span v-if="isRequired" class="red--text">*</span>
        <span>{{ field.attributes.name }}</span>
      </template>
    </v-checkbox>
    <v-select
      clearable
      v-else-if="dropdownResponse"
      v-model="localValue"
      :items="fieldItems(field)"
      v-bind="customFieldErrorAttrs"
      @change="change"
    >
      <template #message="{ message }">
        <span
          :id="errorId(customFieldId)"
          role="alert"
          :data-testid="errorTestid('custom')"
          >{{ message }}</span
        >
      </template>
      <template #label>
        <span v-if="isRequired" class="red--text">*</span>
        <span>{{ field.attributes.name }}</span>
      </template>
    </v-select>
    <v-select
      v-else-if="indicatorResponse"
      v-model="localValue"
      :items="indicatorItems"
      v-bind="customFieldErrorAttrs"
      @change="change"
    >
      <template #message="{ message }">
        <span
          :id="errorId(customFieldId)"
          role="alert"
          :data-testid="errorTestid('custom')"
          >{{ message }}</span
        >
      </template>
      <template #label>
        <span v-if="isRequired" class="red--text">*</span>
        <span>{{ field.attributes.name }}</span>
      </template>
    </v-select>
    <div v-if="dateResponse" class="calendar-overlay">
      <label>
        <span v-if="isRequired" class="red--text">*</span>
        {{ field.attributes.name }}
      </label>
      <flat-pickr
        :name="field.id"
        ref="customFieldCal"
        v-model="localValue"
        :config="dateConfig"
        class="form-control"
        placeholder="Select a Date"
        v-bind="pickrErrorAttrs"
        @on-close="verifyDate('date')"
      />
      <div
        v-if="fieldError('custom')"
        :id="errorId(customFieldId)"
        role="alert"
        class="error--text scheduler-field-error"
        :data-testid="errorTestid('custom')"
      >
        {{ fieldError('custom') }}
      </div>
    </div>
    <div v-if="dateTimeResponse" class="calendar-overlay">
      <label>
        <span v-if="isRequired" class="red--text">*</span>
        {{ field.attributes.name }}
      </label>
      <flat-pickr
        ref="customFieldCal"
        v-model="localValue"
        :config="dateTimeConfig"
        class="form-control"
        placeholder="Select a Date and Time"
        v-bind="pickrErrorAttrs"
        @on-close="verifyDate('datetime')"
      />
      <div
        v-if="fieldError('custom')"
        :id="errorId(customFieldId)"
        role="alert"
        class="error--text scheduler-field-error"
        :data-testid="errorTestid('custom')"
      >
        {{ fieldError('custom') }}
      </div>
    </div>
  </div>
</template>

<script>
import $ from 'jquery'
import fieldErrors from '@/mixins/fieldErrors'
import flatPickr from 'vue-flatpickr-component'

export default {
  components: {
    flatPickr,
  },
  mixins: [fieldErrors],
  computed: {
    checkboxResponse() {
      return this.fieldType === 'checkbox'
    },
    // Stable per template, so `aria-describedby` resolves and two custom fields on the same
    // step never collide. The wrapper's `additional-info-field__<index>` id stays the scroll
    // target; this one addresses the control itself.
    customFieldId() {
      return `custom-field__${this.field.attributes.custom_field_template_id}`
    },
    // Carries the control's own `id` only while an error is showing, so that `aria-describedby`
    // resolves without adding an id to the internal New Inspection / Quote forms, which never
    // receive errors and must render exactly as they did before.
    customFieldErrorAttrs() {
      if (!this.fieldError('custom')) return {}

      return {
        id: this.customFieldId,
        ...this.errorAttrs('custom', this.customFieldId),
      }
    },
    // flat-pickr renders its own <input> AS THE COMPONENT ROOT and declares only
    // value/config/events/disabled, so these fall through as plain attributes onto that input
    // (there is no `input-attrs` prop in v8.1.2).
    pickrErrorAttrs() {
      if (!this.fieldError('custom')) return {}

      return {
        'aria-invalid': 'true',
        'aria-describedby': this.errorId(this.customFieldId),
      }
    },
    dateConfig() {
      return {
        enableTime: false,
        dateFormat: 'Y-m-d',
        altInput: true,
        altFormat: $('#date_format').val().replace(/%/g, ''),
      }
    },
    dateResponse() {
      return this.fieldType === 'date'
    },
    dateTimeConfig() {
      return {
        enableTime: true,
        dateFormat: 'Y-m-d h:i K',
        altInput: true,
        altFormat: $('#date_format').val().replace(/%/g, '') + ' h:i K',
      }
    },
    dateTimeResponse() {
      return this.fieldType === 'datetime'
    },
    dropdownResponse() {
      return this.fieldType === 'dropdown'
    },
    fieldType() {
      return this.field.attributes.response_format
    },
    indicatorItems() {
      return [
        { text: 'N/A', value: 'n/a' },
        { text: 'Yes', value: '1' },
        { text: 'No', value: '0' },
      ]
    },
    indicatorResponse() {
      return this.fieldType === 'indicator'
    },
    isRequired() {
      return this.field.attributes.required && !this.checkboxResponse
    },
    numberResponse() {
      return this.fieldType === 'number'
    },
    paragraphResponse() {
      return this.fieldType === 'paragraph'
    },
    textResponse() {
      return this.fieldType === 'text'
    },
  },
  data() {
    return {
      localValue: null,
    }
  },
  methods: {
    change() {
      this.$emit('input', this.localValue)
    },
    fieldItems() {
      return (this.field.attributes.choices || '').split(',')
    },
    loadValue() {
      if (
        this.checkboxResponse &&
        (this.field.attributes.value === 'f' ||
          this.field.attributes.value === '0')
      ) {
        this.localValue = false
        return
      }

      this.localValue =
        this.field.attributes.formatted_value || this.field.attributes.value
    },
    verifyDate(type) {
      let date = this.$refs.customFieldCal.fp.selectedDates[0]
      if (date) {
        let config = type === 'date' ? this.dateConfig : this.dateTimeConfig
        this.localValue = this.$refs.customFieldCal.fp.formatDate(
          date,
          config.dateFormat
        )
      }

      this.change()
    },
  },
  mounted() {
    this.loadValue()
  },
  props: ['field', 'value'],
}
</script>

<style>
.custom-field {
  width: 100%;
}

/* For some weird reason, this is not working */
/* .custom-field.required label::before,
.custom-field.required label:before {
  content: '* ';
  color: #dc1414;
  font-weight: 300;
} */

.custom-field .calendar-overlay .form-control {
  color: inherit;
}

.custom-field .v-textarea .v-label:not(.v-label--active) {
  margin-left: 0.5em;
  top: 1em;
}

.custom-field textarea {
  max-height: 4em;
  text-indent: 0.25em;
  border: 1px solid rgba(0, 0, 0, 0.3);
}

.custom-field .calendar-overlay label {
  color: rgba(0, 0, 0, 0.54);
}

.custom-field .calendar-overlay input.input::placeholder {
  color: rgba(0, 0, 0, 0.54);
}

.custom-field .calendar-overlay input.input {
  max-height: 32px;
  border-bottom: 1px solid #9e9e9e;
}

.custom-field .calendar-overlay:before {
  bottom: 0;
}
</style>
